import {closeCurrentDialog, openDialog} from 'platform/components';

import {isNil, isNotNil} from 'ramda';

import {ApiError, EntityResourceIds} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {interestRoutes, testIds} from '@dms/routes';

import {composePath, isNotNilOrEmpty, useNavigate} from 'shared/web';

import {EditCustomFieldsValuesForm} from '../../components/EntityCustomFields/components/EditCustomFieldsValuesForm';
import {MandatoryFieldsErrorModal} from '../../components/MandatoryFieldsErrorModal/MandatoryFieldsErrorModal';
import {MandatoryFieldMapValue, mandatoryFieldsMap} from '../../utils/mandatoryFieldsMap';

export interface ValidationError {
  message?: string;
  field?: string;
}

type PathType = '/vehicles/:id/edit' | '/vehicles/:id/edit/:tab';

interface MandatoryFieldsOptions {
  interestId?: string;
  isMiniSale?: boolean;
  customFieldsRecordId?: string;
  customFieldsResourceId?: EntityResourceIds;
}

export function useMandatoryFieldsErrorDialog(
  vehicleId?: string,
  options?: MandatoryFieldsOptions
) {
  const navigate = useNavigate();

  const isMandatoryFieldsError = (error: ApiError) =>
    error.status === 400 &&
    error.data?.errors?.some((errorData) => errorData?.message?.includes('Mandatory'));

  const handleComplete = (field: MandatoryFieldMapValue | undefined, customFieldIds: string[]) => {
    if (options?.isMiniSale) {
      if (!isNotNilOrEmpty(options.interestId)) {
        return closeCurrentDialog();
      }

      return navigate(
        composePath(interestRoutes.detailBuying, {
          params: {id: options.interestId},
        })
      );
    }

    if (customFieldIds.length > 0 && isNotNilOrEmpty(options?.customFieldsRecordId ?? vehicleId)) {
      return openDialog(
        <EditCustomFieldsValuesForm
          recordId={options?.customFieldsRecordId ?? vehicleId ?? ''}
          resourceId={options?.customFieldsResourceId ?? EntityResourceIds.vehicle}
          mandatoryFieldIds={customFieldIds}
          data-testid={testIds.vehicles.detail('vehicle-custom-fields-edit-dialog')}
        />,
        {
          id: 'mandatory-fields-error-modal',
          title: i18n.t('page.generalSettings.labels.customFields'),
        }
      );
    }

    if (!isNotNilOrEmpty(vehicleId)) {
      return closeCurrentDialog();
    }

    if (isNil(field)) {
      return closeCurrentDialog();
    }

    const path = composePath(field.href as PathType, {
      params: {id: vehicleId, tab: isNotNilOrEmpty(field.tab) ? field.tab : undefined},
    });

    return navigate(path);
  };

  const handleMandatoryFieldsError = (validationErrors: ValidationError[]) => {
    const translatedFields = validationErrors.map((item) => item.message ?? '');
    const fields = validationErrors.flatMap((item) =>
      isNotNilOrEmpty(item.field) ? [item.field] : []
    );
    const mappedField = fields.map((field) => mandatoryFieldsMap[field]).find(isNotNil);
    const customFieldIds = fields.filter((field) => isNil(mandatoryFieldsMap[field]));

    openDialog(
      <MandatoryFieldsErrorModal
        mandatoryFields={isNil(mappedField) ? [] : [mappedField]}
        translatedFields={translatedFields}
        onSubmit={() => handleComplete(mappedField, customFieldIds)}
      />,
      {
        id: 'mandatory-fields-error-modal',
        title: i18n.t('entity.mandatoryFields.errorModal.title'),
      }
    );
  };

  return {
    isMandatoryFieldsError,
    handleMandatoryFieldsError,
  };
}
